import type { WorkerChatReadiness } from './worker-chat-readiness';

/**
 * Active operational Worker choices are a projection over canonical readiness.
 *
 * This deliberately does not mutate, delete, reroute, or relabel Worker history.
 * Historical consumers keep the full Worker/readiness/message records; only
 * current roster/recipient surfaces should consume this projection.
 */
export interface ActiveWorkerProjection {
  readonly workers: readonly WorkerChatReadiness[];
  readonly selectedWorkerIds: ReadonlySet<string>;
  /** Previously selected Workers that are no longer current usable choices. */
  readonly removedSelectedWorkerIds: readonly string[];
}

const QUEUEABLE_AVAILABILITY = new Set<WorkerChatReadiness['availability']>([
  'busy',
  'waiting',
  'cannot_interleave',
]);

/**
 * True only when canonical readiness proves that the Worker is online and
 * addressable now, or is online and explicitly queueable behind current work.
 *
 * Unknown never becomes online here: an unknown/missing conversational state,
 * runtime, terminal, session, or machine route remains excluded.
 */
export function isActiveUsableWorker(worker: WorkerChatReadiness): boolean {
  if (!worker.listed || worker.machineConnection !== 'connected') return false;

  if (worker.availability === 'available') {
    return worker.delivery === 'now' && worker.route !== null;
  }

  return worker.delivery === 'after_current_turn'
    && QUEUEABLE_AVAILABILITY.has(worker.availability);
}

/**
 * #584 roster visibility: the Worker is currently online, whether Idle or busy.
 *
 * Wider than {@link isActiveUsableWorker} on purpose: an online Idle Worker whose
 * AI session has not started yet still belongs on the active roster. It still
 * never invents online state: a missing, stale, or disconnected heartbeat, an
 * offline/retired Worker, or a Worker without a workforce session stays hidden.
 */
export function isActiveRosterWorker(worker: WorkerChatReadiness): boolean {
  return worker.listed
    && worker.machineConnection === 'connected'
    && worker.workerStatus !== 'offline';
}

/**
 * Keep only roster entries whose Worker canonical readiness proves online, in
 * their original order. An entry with no readiness evidence is hidden, never
 * assumed online. History records themselves are not touched.
 */
export function activeRosterEntries<T extends { readonly workerId: string }>(
  entries: readonly T[],
  readiness: readonly WorkerChatReadiness[],
): T[] {
  const online = new Set(readiness.filter(isActiveRosterWorker).map((worker) => worker.workerId));
  return entries.filter((entry) => online.has(entry.workerId));
}

/** Current selectable/active Workers only, preserving canonical Worker order. */
export function activeWorkerParticipants(
  readiness: readonly WorkerChatReadiness[],
): readonly WorkerChatReadiness[] {
  return readiness.filter(isActiveUsableWorker);
}

/**
 * Project current choices and reconcile an existing selection without
 * substituting another Worker. Historical records are intentionally untouched.
 */
export function projectActiveWorkers(
  readiness: readonly WorkerChatReadiness[],
  selectedWorkerIds: ReadonlySet<string> = new Set<string>(),
): ActiveWorkerProjection {
  const workers = activeWorkerParticipants(readiness);
  const activeIds = new Set(workers.map((worker) => worker.workerId));
  const selected = new Set<string>();
  const removed: string[] = [];

  for (const workerId of selectedWorkerIds) {
    if (activeIds.has(workerId)) selected.add(workerId);
    else removed.push(workerId);
  }

  return {
    workers,
    selectedWorkerIds: selected,
    removedSelectedWorkerIds: removed,
  };
}
